/**
 * StatusBadge Component
 * Consistent status display across the application.
 * 
 * Usage:
 *   <StatusBadge status="approved" />
 *   <StatusBadge status="pending" type="leave" />
 *   <StatusBadge status="active" size="sm" />
 */
import { Badge } from '../ui/badge';

// Status to variant mapping
const STATUS_VARIANTS = {
  // Approval statuses
  pending: 'secondary',
  submitted: 'secondary',
  manager_approved: 'outline',
  hr_approved: 'outline',
  approved: 'default',
  rejected: 'destructive',
  cancelled: 'secondary',
  
  // General statuses
  active: 'default',
  inactive: 'secondary',
  terminated: 'destructive',
  on_leave: 'outline',
  
  // Invoice statuses
  draft: 'secondary',
  sent: 'outline',
  paid: 'default',
  overdue: 'destructive',
  partial: 'outline',
  
  // Asset statuses
  available: 'default',
  assigned: 'outline',
  maintenance: 'secondary',
  retired: 'destructive',
  
  // Recruitment statuses
  open: 'default',
  closed: 'secondary',
  on_hold: 'outline',
  filled: 'default',
  
  // Default
  default: 'secondary'
};

// Human-readable labels
const STATUS_LABELS = {
  manager_approved: 'Manager Approved',
  hr_approved: 'HR Approved',
  on_leave: 'On Leave',
  // Add more as needed
};

export function getStatusVariant(status) {
  return STATUS_VARIANTS[status?.toLowerCase()] || STATUS_VARIANTS.default;
}

export function getStatusLabel(status) {
  const label = STATUS_LABELS[status?.toLowerCase()];
  if (label) return label;
  
  // Convert snake_case to Title Case
  return status
    ?.replace(/_/g, ' ')
    .replace(/\b\w/g, c => c.toUpperCase())
    || 'Unknown';
}

export function StatusBadge({ 
  status, 
  type: _type,
  size = 'default',
  className = '',
  showIcon: _showIcon = false 
}) {
  if (!status) return null;

  const variant = getStatusVariant(status);
  const label = getStatusLabel(status);
  
  const sizeClasses = {
    sm: 'text-xs px-1.5 py-0',
    default: '',
    lg: 'text-sm px-3 py-1'
  };

  return (
    <Badge 
      variant={variant} 
      className={`${sizeClasses[size]} ${className}`}
    >
      {label}
    </Badge>
  );
}

export default StatusBadge;
